import { SEARCH_RESULTS_PATH } from '../config/site';
import { isBookingMetierSlug } from '../config/metierPathSlugs';
import { isWidgetCheckoutFrame, isWidgetThankYouFrame } from '../utils/widgetFrameUtils';

export interface NativeHeaderConfig {
  /** Titre affiché dans le header natif. Null = logo Rintintin. */
  title: string | null;
  /** Clé i18n (namespace `native`) pour le titre du header. */
  titleKey: string | null;
  /** Affiche la flèche retour. Null = déterminé automatiquement. */
  showBack: boolean | null;
  /**
   * Route parente explicite pour le bouton retour.
   * Si null, on utilise navigate(-1) (historique).
   * Utilisé quand l'utilisateur peut arriver via une notification
   * sans historique interne (ex: /booking-confirmation → /reservations).
   */
  backPath: string | null;
}

const LOCALE_PATH_PREFIXES = ['en', 'es', 'de', 'it', 'nl'] as const;

/** Fiche publique `/pro/:slug` (y compris `/en/pro/:slug`), pas book/service/forfait. */
export function isProPublicProfilePath(pathname: string): boolean {
  const segments = pathname.replace(/\/$/, '').split('/').filter(Boolean);
  let i = 0;
  const head = segments[0];
  if (head && (LOCALE_PATH_PREFIXES as readonly string[]).includes(head)) {
    i = 1;
  }
  if (segments[i] !== 'pro') return false;
  return segments.length === i + 2;
}

/** Landings SEO Booking : footer marketing visible en mobile (<768px). */
export function isPublicSeoBookingPath(pathname: string): boolean {
  if (pathname === '/') return true;
  const segments = pathname.replace(/\/$/, '').split('/').filter(Boolean);
  let i = 0;
  const head = segments[0];
  if (head && (LOCALE_PATH_PREFIXES as readonly string[]).includes(head)) {
    i = 1;
    // #1118 v3 : accueil localisé (/en, /en/, /es…) = landing SEO → footer visible en mobile.
    if (segments.length === 1) return true;
  }
  if (segments[i] === 'services') {
    const slugCount = segments.length - i - 1;
    return slugCount === 0 || slugCount === 1 || slugCount === 2;
  }
  if (segments[i] === 'blog') {
    return true;
  }
  const tail = segments[i];
  return Boolean(tail && segments.length - i === 1 && isBookingMetierSlug(tail));
}

export interface ShellRouteContext {
  pathname: string;
  isProListPage: boolean;
  isMessagingPage: boolean;
  shouldHideChrome: boolean;
  shouldHideFooter: boolean;
  /** Footer marketing affiché sous 768px (pages SEO publiques uniquement). */
  showMarketingFooterOnMobile: boolean;
  /** Desktop : différer le footer marketing jusqu’à stabilisation (fiches /pro). */
  deferDesktopFooterUntilStable: boolean;
  showNavbar: boolean;
  showEmployeeBanner: boolean;
  showBottomNav: boolean;
  /** Affiche le header natif (masqué sur les écrans racines du bottom nav). */
  showNativeHeader: boolean;
  nativeHeader: NativeHeaderConfig;
}

/** Écrans racines natifs : pas de header (bottom nav flottante seulement). */
const NATIVE_HEADER_HIDDEN_ROUTES = new Set(['/', '/messaging', '/notifications', '/profile']);

/** Routes racines de la bottom nav → logo, pas de retour. */
const ROOT_ROUTES = new Set(['/', '/pros', '/messaging', '/notifications', '/profile']);

/** Mapping route → clé i18n titre + route parente explicite (préfixe matching). */
const ROUTE_CONFIG: Array<{ prefix: string; titleKey: string | null; backPath?: string | null }> = [
  { prefix: '/profile/settings', titleKey: 'headers.settings', backPath: '/profile' },
  { prefix: '/reservations', titleKey: 'headers.reservations', backPath: '/' },
  { prefix: '/profile', titleKey: 'headers.account' },
  { prefix: '/favorites', titleKey: 'headers.favorites', backPath: '/profile' },
  { prefix: '/messaging', titleKey: 'headers.messages' },
  { prefix: '/animal', titleKey: 'headers.animals', backPath: '/profile' },
  { prefix: '/mes-forfaits', titleKey: 'headers.packages', backPath: '/profile' },
  { prefix: '/mes-abonnements', titleKey: 'headers.subscriptions', backPath: '/profile' },
  { prefix: '/credits', titleKey: 'headers.credits', backPath: '/profile' },
  { prefix: '/mes-cadeaux', titleKey: 'headers.giftCodes', backPath: '/profile' },
  { prefix: '/cadeau/', titleKey: 'headers.giftCard', backPath: '/mes-cadeaux' },
  { prefix: '/quotes', titleKey: 'headers.quotes', backPath: '/profile' },
  { prefix: '/mes-suivis', titleKey: 'headers.reports', backPath: '/profile' },
  { prefix: '/compte-rendus', titleKey: 'headers.reports', backPath: '/profile' },
  { prefix: '/checkout', titleKey: 'headers.checkout', backPath: null },
  { prefix: '/booking-confirmation', titleKey: 'headers.confirmation', backPath: '/reservations' },
  { prefix: '/login', titleKey: 'headers.signIn', backPath: '/' },
  { prefix: '/signup', titleKey: 'headers.signUp', backPath: '/' },
  { prefix: '/pro/', titleKey: null },
];

function isNativeAuthPage(pathname: string): boolean {
  return pathname === '/login' || pathname === '/signup';
}

function isNativeAnimalAddFlow(pathname: string, search: string): boolean {
  return pathname === '/animal' && new URLSearchParams(search).get('add') === '1';
}

function resolveNativeHeader(pathname: string): NativeHeaderConfig {
  const isRoot = ROOT_ROUTES.has(pathname);
  if (isRoot) return { title: null, titleKey: null, showBack: false, backPath: null };

  const match = ROUTE_CONFIG.find((r) => pathname.startsWith(r.prefix));
  return {
    title: null,
    titleKey: match?.titleKey ?? null,
    showBack: true,
    backPath: match?.backPath !== undefined ? match.backPath : null,
  };
}

export function getShellRouteContext(
  pathname: string,
  search: string,
  isMobileLayout: boolean
): ShellRouteContext {
  const isProListPage = pathname === SEARCH_RESULTS_PATH;
  const isMessagingPage = pathname === '/messaging';
  const isEmbedPage = pathname.startsWith('/embed/');
  const isPublicCarnet = pathname.startsWith('/carnet/');
  const isDevLabPage = pathname.startsWith('/dev/');
  const isWidgetCheckout = isWidgetCheckoutFrame(search);
  const isWidgetThankYou = isWidgetThankYouFrame(search);
  const shouldHideChrome =
    isEmbedPage || isDevLabPage || isWidgetCheckout || isWidgetThankYou || isPublicCarnet;

  const shouldHideFooter =
    isProListPage || (isMessagingPage && isMobileLayout) || shouldHideChrome;

  const showMarketingFooterOnMobile =
    isMobileLayout &&
    !shouldHideFooter &&
    isPublicSeoBookingPath(pathname);

  const deferDesktopFooterUntilStable =
    !isMobileLayout && !shouldHideFooter && isProPublicProfilePath(pathname);

  const nativeAnimalAddFlow = isNativeAnimalAddFlow(pathname, search);
  const nativeAuthPage = isNativeAuthPage(pathname);

  return {
    pathname,
    isProListPage,
    isMessagingPage,
    shouldHideChrome,
    shouldHideFooter,
    showMarketingFooterOnMobile,
    deferDesktopFooterUntilStable,
    showNavbar: !isProListPage && !shouldHideChrome,
    showEmployeeBanner: !shouldHideChrome,
    showBottomNav: !shouldHideChrome && !nativeAnimalAddFlow && !nativeAuthPage,
    showNativeHeader:
      !shouldHideChrome &&
      !NATIVE_HEADER_HIDDEN_ROUTES.has(pathname) &&
      !nativeAnimalAddFlow,
    nativeHeader: resolveNativeHeader(pathname),
  };
}
